import { Controller } from "@hotwired/stimulus"

// Recurrence picker on the New Meeting form. Mirrors the Google
// Calendar dropdown: smart labels that adapt to the picked date
// ("Repeats every Wednesday" when held_on is a Wednesday). Toggles
// the end-condition fieldset visibility based on whether a frequency
// was picked. Validates the picked end-condition against the meeting's
// held_on date so a series can't end before it starts; blocks form
// submit when invalid.
//
// Pairs with the form section in app/views/meetings/_form.html.erb
// guarded by `action_name == "new"`. The inputs aren't `disabled` —
// flatpickr's altInput doesn't play nicely with a re-enabled
// underlying input — so we let them all stay interactive and just
// keep the radios + values in sync as the user moves around.
export default class extends Controller {
  static targets = [
    "freqSelect",
    "endSection",
    "endsOnRadio",
    "endsOnInput",
    "afterNRadio",
    "occurrenceInput",
    "projectEndRow",
    "errorMessage",
    "dateInput"
  ]
  static values = { weekday: String }

  connect() {
    this._syncEndSection()
    this._syncProjectEndVisibility()
    // Hook the form's submit so we can block invalid recurrence
    // combinations before they hit the controller.
    this.form = this.element.closest("form")
    if (this.form) {
      this._submitHandler = (e) => this._onSubmit(e)
      this.form.addEventListener("submit", this._submitHandler)
    }
  }

  disconnect() {
    if (this.form && this._submitHandler) {
      this.form.removeEventListener("submit", this._submitHandler)
    }
  }

  // Held-on date changed → recompute "Repeats every Wednesday" etc.
  dateChanged(event) {
    const value = event.target.value
    if (!value) return
    const date = new Date(`${value}T00:00:00`)
    if (Number.isNaN(date.getTime())) return
    const weekday = date.toLocaleDateString(undefined, { weekday: "long" })
    const dayOfMonth = date.getDate()
    this._relabelOptions(weekday, dayOfMonth)
    this._syncProjectEndVisibility()
    this.validate()
  }

  // Frequency dropdown changed → show or hide the end-condition box.
  frequencyChanged() {
    this._syncEndSection()
  }

  // Radio inside the end-condition box changed. No-op for now; the
  // companion inputs stay interactive at all times.
  endKindChanged() {
    this.validate()
  }

  // User typed in the "On" date picker or focused it → auto-pick the
  // "On" radio so submitting works without an extra click.
  pickOnDate() {
    if (this.hasEndsOnRadioTarget) this.endsOnRadioTarget.checked = true
  }

  // User typed in the "After N" input or focused it → auto-pick the
  // "After" radio.
  pickAfterN() {
    if (this.hasAfterNRadioTarget) this.afterNRadioTarget.checked = true
  }

  // Validate the currently-picked end condition. Sets inline error
  // text (or clears it). Returns true when valid.
  validate() {
    if (!this.hasEndSectionTarget) return true
    if (this.endSectionTarget.classList.contains("hidden")) {
      this._setError(null)
      return true
    }

    const heldOn = this._heldOnDate()
    const picked = this._currentEndKind()

    if (!picked) {
      this._setError("Pick when the series ends — a date or one of the options below.")
      return false
    }

    if (picked === "on_date") {
      const endsOn = this._endsOnDate()
      if (!endsOn) {
        this._setError("Pick an end date or choose a different option.")
        return false
      }
      if (heldOn && endsOn <= heldOn) {
        this._setError("End date must be after the meeting's held-on date.")
        return false
      }
    } else if (picked === "after_n") {
      const n = parseInt(this.occurrenceInputTarget.value, 10)
      if (!Number.isFinite(n) || n < 2) {
        this._setError("Must repeat at least 2 times.")
        return false
      }
    } else if (picked === "project_end") {
      const projectEnd = this._projectEndDate()
      if (heldOn && projectEnd && projectEnd <= heldOn) {
        this._setError("Project completion date is on or before this meeting — pick another option.")
        return false
      }
    }

    this._setError(null)
    return true
  }

  // ─── private ──────────────────────────────────────────────────

  _onSubmit(event) {
    if (!this.validate()) {
      event.preventDefault()
      event.stopPropagation()
      this.errorMessageTarget.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  _currentEndKind() {
    const checked = this.element.querySelector('input[name="recurrence[end_kind]"]:checked')
    return checked ? checked.value : null
  }

  _heldOnDate() {
    if (!this.hasDateInputTarget) return null
    const v = this.dateInputTarget.value
    if (!v) return null
    const d = new Date(`${v}T00:00:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  _endsOnDate() {
    if (!this.hasEndsOnInputTarget) return null
    const v = this.endsOnInputTarget.value
    if (!v) return null
    const d = new Date(`${v}T00:00:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  _projectEndDate() {
    if (!this.hasProjectEndRowTarget) return null
    const v = this.projectEndRowTarget.dataset.projectEndDate
    if (!v) return null
    const d = new Date(`${v}T00:00:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  _setError(msg) {
    if (!this.hasErrorMessageTarget) return
    if (msg) {
      this.errorMessageTarget.textContent = msg
      this.errorMessageTarget.classList.remove("hidden")
    } else {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  // Hide the "Project completion date" row when held_on is at or
  // past that date — picking it then would yield zero future
  // meetings. If currently selected, clear it so the user must
  // re-pick a valid end condition.
  _syncProjectEndVisibility() {
    if (!this.hasProjectEndRowTarget) return
    const heldOn = this._heldOnDate()
    const projectEnd = this._projectEndDate()
    const shouldHide = heldOn && projectEnd && projectEnd <= heldOn
    this.projectEndRowTarget.classList.toggle("hidden", !!shouldHide)
    if (shouldHide) {
      const radio = this.projectEndRowTarget.querySelector('input[type="radio"]')
      if (radio && radio.checked) radio.checked = false
    }
  }

  _syncEndSection() {
    const freq = this.freqSelectTarget.value
    if (freq === "none" || freq === "") {
      this.endSectionTarget.classList.add("hidden")
    } else {
      this.endSectionTarget.classList.remove("hidden")
    }
  }

  _relabelOptions(weekday, dayOfMonth) {
    Array.from(this.freqSelectTarget.options).forEach((opt) => {
      const tpl = opt.dataset.weekdayLabel
      if (!tpl) return
      opt.textContent = tpl
        .replace("{weekday}", weekday)
        .replace("{day_of_month}", dayOfMonth)
    })
  }
}
